/**
 * Assets Objects — content bundle for the WAC UBP calculator.
 *
 * Source of truth: Confluence "Assets objects: Content spec for WAC Calculator"
 * https://hello.atlassian.net/wiki/spaces/acbp/pages/7129383223
 *
 * Content DRI: Belle Craig · Meter CD: Sarah Campbell
 *
 * NOTE ON PROFILE KEYS: The `UsageType` enum has stable keys
 * `exploring | adopting | scaling` for URL/state/analytics compatibility.
 * The Assets spec's visible labels are `Emerging | Adopting | Scaling`,
 * which are the customer-facing strings shown on the profile cards.
 * The keys are internal identifiers only — do NOT rename them.
 *
 * Fields we haven't finalized are simply OMITTED from this file (most
 * are optional at the type level) so nothing renders for them. Do NOT
 * stub placeholder strings — omit the field entirely instead. There is
 * no `PLACEHOLDER` / `todoCopy` helper exported from this module by
 * design.
 *
 * DO NOT edit unrelated meter files when updating this one — the atomic
 * per-meter file split exists to prevent merge conflicts between authors.
 *
 * i18n: every copy field is wrapped in `defineMessage` with a literal
 * `id` and `defaultMessage`. The extractor is a static AST analyzer —
 * see the i18n contract in `meter-content.types.ts`. Keep both fields
 * literal; never build an id from a variable.
 */

import { defineMessage } from 'react-intl';

import { wacToken } from '@/libraries/design/foundations';

import type { MeterContent } from './meter-content.types';

export const ASSETS_OBJECTS_CONTENT: MeterContent = {
	description: defineMessage({
		id: 'ubp.calculator.usage.assetsObjects.description',
		defaultMessage:
			'Use Assets to store and manage your objects and connect them to your work across Atlassian.',
	}),
	needsStep: {
		// Step 2 "Do you know your usage?" question — Assets objects copy.
		doYouKnowQuestion: defineMessage({
			id: 'ubp.calculator.needs.assetsObjects.doYouKnowQuestion',
			defaultMessage: 'Know how many objects you’ll need?',
		}),
		// Assets spec: Step 2 primary CTA is "Next" (not the default
		// "Get estimate" used by other meters).
		nextCta: defineMessage({
			id: 'ubp.calculator.needs.assetsObjects.nextCta',
			defaultMessage: 'Next',
		}),
	},
	estimateStep: {
		subtitle: defineMessage({
			id: 'ubp.calculator.estimate.assetsObjects.subtitle',
			defaultMessage:
				'Based on what you’ve told us, here’s a summary of how many Assets Objects you need, and how you can use them.',
		}),
		compareCta: defineMessage({
			id: 'ubp.calculator.estimate.assetsObjects.compareCta',
			defaultMessage: 'Explore plans',
		}),
		// TEMPORARY: All meters currently share this Orange Logic asset
		// while the content team finalizes per-meter hero imagery.
		image: {
			src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/bb1q20l5xjp2i85j55ou317euquo3rv3.png',
			alt: '',
			imageBackgroundColor: wacToken('color.extended.blue100'),
		},
		// ContentBlock copy per revised design direction (2026-07-16):
		// reframe Card 1 around ROI + built-in allowances with the
		// "Explore plans" CTA. Shared across all non-CSR meters.
		contentBlock: {
			title: defineMessage({
				id: 'ubp.calculator.estimate.assetsObjects.contentBlock.title',
				defaultMessage: 'Maximize your ROI with flexible, usage-based plans',
			}),
			intro: defineMessage({
				id: 'ubp.calculator.estimate.assetsObjects.contentBlock.intro',
				defaultMessage:
					'With Atlassian collections and apps, built-in allowances align your costs with your real-world usage.',
			}),
			listItems: [
				defineMessage({
					id: 'ubp.calculator.estimate.assetsObjects.contentBlock.listItems.0',
					defaultMessage: 'Optimize your spend',
				}),
				defineMessage({
					id: 'ubp.calculator.estimate.assetsObjects.contentBlock.listItems.1',
					defaultMessage: 'Plan with confidence',
				}),
				defineMessage({
					id: 'ubp.calculator.estimate.assetsObjects.contentBlock.listItems.2',
					defaultMessage: 'Do more with less',
				}),
			],
			href: '/collections',
		},
	},
	allowanceStep: {
		// Step 4 primary CTA — "Compare" per the latest design direction
		// (all meters share this comparison-focused verb on this step).
		nextCta: defineMessage({
			id: 'ubp.calculator.allowance.assetsObjects.nextCta',
			defaultMessage: 'Compare',
		}),
	},
	summaryStep: {
		// Step 5 header — per Assets objects Confluence spec.
		title: defineMessage({
			id: 'ubp.calculator.summary.assetsObjects.title',
			defaultMessage: 'Review usage estimate and compare',
		}),
		subtitle: defineMessage({
			id: 'ubp.calculator.summary.assetsObjects.subtitle',
			defaultMessage:
				'Based on what you’ve told us, here’s a summary of how many Assets Objects you need, and how you can use them.',
		}),
		orgNeedsCard: {
			eyebrow: defineMessage({
				id: 'ubp.calculator.summary.assetsObjects.orgNeedsCard.eyebrow',
				defaultMessage: 'YOUR ORGANIZATION NEEDS',
			}),
			headlineSubtext: defineMessage({
				id: 'ubp.calculator.summary.assetsObjects.orgNeedsCard.headlineSubtext',
				defaultMessage: 'Assets objects',
			}),
		},
		whatsCoveredCard: {
			eyebrow: defineMessage({
				id: 'ubp.calculator.summary.assetsObjects.whatsCoveredCard.eyebrow',
				defaultMessage: 'WHAT’S COVERED',
			}),
			headlineSubtext: defineMessage({
				id: 'ubp.calculator.summary.assetsObjects.whatsCoveredCard.headlineSubtext',
				defaultMessage: 'Assets objects included monthly',
			}),
			bodyCovered: defineMessage({
				id: 'ubp.calculator.summary.assetsObjects.whatsCoveredCard.bodyCovered',
				defaultMessage: 'Your Assets objects are covered within the allowance.',
			}),
			bodyOverage: defineMessage({
				id: 'ubp.calculator.summary.assetsObjects.whatsCoveredCard.bodyOverage',
				defaultMessage:
					'There’s a gap of {overage} Assets objects. You may incur extra usage charges of {cost}.',
			}),
			listItemsCovered: [
				defineMessage({
					id: 'ubp.calculator.summary.assetsObjects.whatsCoveredCard.listItemsCovered.0',
					defaultMessage: 'Higher plans provide more allowances',
				}),
				defineMessage({
					id: 'ubp.calculator.summary.assetsObjects.whatsCoveredCard.listItemsCovered.1',
					defaultMessage:
						'Track your extra usage via your capabilities subscription details page in admin hub',
				}),
				defineMessage({
					id: 'ubp.calculator.summary.assetsObjects.whatsCoveredCard.listItemsCovered.2',
					defaultMessage: 'Set an extra usage limit to stay in budget',
				}),
			],
			listItemsOverage: [
				defineMessage({
					id: 'ubp.calculator.summary.assetsObjects.whatsCoveredCard.listItemsOverage.0',
					defaultMessage: 'Higher plans provide more allowances',
				}),
				defineMessage({
					id: 'ubp.calculator.summary.assetsObjects.whatsCoveredCard.listItemsOverage.1',
					defaultMessage:
						'Track your extra usage via your capabilities subscription details page in admin hub',
				}),
				defineMessage({
					id: 'ubp.calculator.summary.assetsObjects.whatsCoveredCard.listItemsOverage.2',
					defaultMessage: 'Set an extra usage limit to stay in budget',
				}),
			],
		},
	},
	usageProfiles: {
		exploring: {
			label: defineMessage({
				id: 'ubp.calculator.needs.assetsObjects.exploring.label',
				defaultMessage: 'Emerging',
			}),
			description: defineMessage({
				id: 'ubp.calculator.needs.assetsObjects.exploring.description',
				defaultMessage:
					'Start tracking and managing the people and things that are important to your business.',
			}),
			usageRange: defineMessage({
				id: 'ubp.calculator.needs.assetsObjects.exploring.usageRange',
				defaultMessage: '0–3 objects per user',
			}),
			list: [
				defineMessage({
					id: 'ubp.calculator.needs.assetsObjects.exploring.list.0',
					defaultMessage: 'Track team members or equipment',
				}),
				defineMessage({
					id: 'ubp.calculator.needs.assetsObjects.exploring.list.1',
					defaultMessage: 'Link objects to work',
				}),
			],
			valueProp: defineMessage({
				id: 'ubp.calculator.needs.assetsObjects.exploring.valueProp',
				defaultMessage:
					'Most teams start here and scale up as they connect more of their business to Assets.',
			}),
		},
		adopting: {
			label: defineMessage({
				id: 'ubp.calculator.needs.assetsObjects.adopting.label',
				defaultMessage: 'Adopting',
			}),
			description: defineMessage({
				id: 'ubp.calculator.needs.assetsObjects.adopting.description',
				defaultMessage:
					'Track a growing number of objects like people, places, and products. Good for teams tracking important relationships between objects.',
			}),
			usageRange: defineMessage({
				id: 'ubp.calculator.needs.assetsObjects.adopting.usageRange',
				defaultMessage: '4–10 objects per user',
			}),
			list: [
				defineMessage({
					id: 'ubp.calculator.needs.assetsObjects.adopting.list.0',
					defaultMessage: 'Map multiple structured object relationships',
				}),
				defineMessage({
					id: 'ubp.calculator.needs.assetsObjects.adopting.list.1',
					defaultMessage: 'Model business processes and org structures',
				}),
			],
			valueProp: defineMessage({
				id: 'ubp.calculator.needs.assetsObjects.adopting.valueProp',
				defaultMessage:
					'Teams at this usage level often manage many object schemas across multiple departments.',
			}),
		},
		scaling: {
			label: defineMessage({
				id: 'ubp.calculator.needs.assetsObjects.scaling.label',
				defaultMessage: 'Scaling',
			}),
			description: defineMessage({
				id: 'ubp.calculator.needs.assetsObjects.scaling.description',
				defaultMessage:
					'Track everything and anything that matters to your business, from laptops to llamas.',
			}),
			usageRange: defineMessage({
				id: 'ubp.calculator.needs.assetsObjects.scaling.usageRange',
				defaultMessage: '11–50 objects per user',
			}),
			list: [
				defineMessage({
					id: 'ubp.calculator.needs.assetsObjects.scaling.list.0',
					defaultMessage: 'Manage complex cross-team dependencies',
				}),
				defineMessage({
					id: 'ubp.calculator.needs.assetsObjects.scaling.list.1',
					defaultMessage: 'Add significant breadth and depth to the teamwork graph',
				}),
			],
			valueProp: defineMessage({
				id: 'ubp.calculator.needs.assetsObjects.scaling.valueProp',
				defaultMessage:
					'Organizations at this scale connect everything and anything that matters to their business.',
			}),
		},
	},
	valueProps: [
		{
			title: defineMessage({
				id: 'ubp.calculator.estimate.assetsObjects.valueProps.0.title',
				defaultMessage: 'Track anything that matters',
			}),
			description: defineMessage({
				id: 'ubp.calculator.estimate.assetsObjects.valueProps.0.description',
				defaultMessage:
					'Store and manage any type of business object, from equipment to contracts, locations to vendors, in one structured, searchable place.',
			}),
		},
		{
			title: defineMessage({
				id: 'ubp.calculator.estimate.assetsObjects.valueProps.1.title',
				defaultMessage: 'Stay organized at scale',
			}),
			description: defineMessage({
				id: 'ubp.calculator.estimate.assetsObjects.valueProps.1.description',
				defaultMessage:
					'Manage records and object relationships with room to grow as your team needs evolve.',
			}),
		},
		{
			title: defineMessage({
				id: 'ubp.calculator.estimate.assetsObjects.valueProps.2.title',
				defaultMessage: 'Connect work across Atlassian',
			}),
			description: defineMessage({
				id: 'ubp.calculator.estimate.assetsObjects.valueProps.2.description',
				defaultMessage:
					'Link objects to your issues, requests, and pages, with full context, always.',
			}),
		},
	],
};
